import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { apiPatch, apiPost, operationsClient } from '@/lib/api';
import { paymentScheduleEndpoints } from '@/lib/api/endpoints';
import { paymentScheduleKeys } from '@/lib/queryKeys';
import type { PaymentSchedule, UpdatePaymentScheduleInput } from '@/types/paymentSchedule';
import { invalidatePaymentScheduleEffects } from './invalidatePaymentScheduleEffects';

export function useUpdatePaymentSchedule(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<PaymentSchedule, UpdatePaymentScheduleInput>({
    mutationFn: (dto) =>
      apiPatch<PaymentSchedule, UpdatePaymentScheduleInput>(
        operationsClient,
        paymentScheduleEndpoints.update(id),
        dto,
      ),
    onSuccess: (schedule) => {
      void queryClient.invalidateQueries({
        queryKey: paymentScheduleKeys.detail(id),
      });
      invalidatePaymentScheduleEffects(queryClient, schedule.jobId);
      toast.success('Payment schedule updated');
    },
  });
}

export function usePausePaymentSchedule(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<PaymentSchedule, void>({
    mutationFn: () =>
      apiPost<PaymentSchedule, undefined>(
        operationsClient,
        paymentScheduleEndpoints.pause(id),
        undefined,
      ),
    onSuccess: (schedule) => {
      void queryClient.invalidateQueries({
        queryKey: paymentScheduleKeys.detail(id),
      });
      invalidatePaymentScheduleEffects(queryClient, schedule.jobId);
      toast.success('Payment schedule paused');
    },
  });
}

export function useResumePaymentSchedule(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<PaymentSchedule, void>({
    mutationFn: () =>
      apiPost<PaymentSchedule, undefined>(
        operationsClient,
        paymentScheduleEndpoints.resume(id),
        undefined,
      ),
    onSuccess: (schedule) => {
      void queryClient.invalidateQueries({
        queryKey: paymentScheduleKeys.detail(id),
      });
      invalidatePaymentScheduleEffects(queryClient, schedule.jobId);
      toast.success('Payment schedule resumed');
    },
  });
}
